import React, {useState} from 'react';
import {FieldProp, Field} from '../../types';
import {Modal} from '../Modal';
import {ActivityButton} from '../ActivityButton';
import {Form, FormState, initialFormState} from '../Form';
import {fieldValueLabel, formIsBusy, validate, parseMergeTag} from '../../_helpers/form';
import {FormContainer} from '../FormContainer';
import {NestedFormExcelUpload} from './NestedFormExcelUpload';

type NestedFormGridProps = FieldProp & {
    additionalButtons: JSX.Element[] | undefined;   // additional list of buttons to display next to "Add ..."
                                                    // it is the parent's responsibility to handle any button interactions
};

export const NestedFormGrid: React.FunctionComponent<NestedFormGridProps> = ({
                                                                                 field,
                                                                                 fieldState,
                                                                                 displayOnly = false,
                                                                                 value,
                                                                                 onChange,
                                                                                 additionalButtons,
                                                                                 showAdministrativeFields = false,
                                                                                 ...rest
                                                                             }): JSX.Element => {

    const initialFieldValues = field.nestedForm.fields.reduce((acc: any, field: Field) => {
        const {form, formState} = rest;
        // @ts-ignore
        return {...acc, [field.name]: parseMergeTag(field.initialValue, form, formState)}
    }, {});

    const [formState, setFormState] = useState<FormState>({
        ...initialFormState,
        fieldValues: initialFieldValues
    });
    const [formIsSubmitting, setFormIsSubmitting] = useState<boolean>(false);
    const [showAddRecordModal, setShowAddRecordModal] = useState<boolean>(false);
    const [showExcelUploadModal, setShowExcelUploadModal] = useState<boolean>(false);
    const [nestedFormExcelUploadErrorMessage, setNestedFormExcelUploadErrorMessage] = useState('');

    let hasExcelUpload = false;
    if (field.flowCustomDisplayComponentConfig.hasOwnProperty('excelUpload')) {
        hasExcelUpload = true;
    }

    const {
        touched,
        pristine,
        validating,
        valid,
        invalid,
        errors,
    } = fieldState;

    const classNames = [
        touched && 'is-touched',
        pristine && 'is-pristine',
        validating && 'is-validating',
        valid && 'is-valid',
        invalid && 'is-invalid',
    ]
        .filter(Boolean)
        .join(' ');

    const localDisplayOnly = field.hasOwnProperty('gpnfDisableEdit') ? field.gpnfDisableEdit : displayOnly;

    return (
        <div className={classNames}>

            {errors && errors.map((error: string, index: number) => (
                <div key={index} className="invalid-feedback">
                    {error}
                </div>
            ))}
            {nestedFormExcelUploadErrorMessage &&
                nestedFormExcelUploadErrorMessage
                    .split(/\n/)
                    .map((m: string, index: number) => (
                        <div key={index} className="invalid-feedback">
                            {m}
                        </div>
                    ))}

            <div className="nested-table-container">
                {/* the ternary operator while dumb, removes the "empty text node in table" warning thrown by react: https://stackoverflow.com/a/53519842 */}
                {value && value.length > 0 &&
                    value.map((item: any, index: number) => (
                        <tr key={index}>
                            {field.gpnfFields.map((fieldId: number) => {
                                const nestedField = field.nestedForm.fieldMap[fieldId];
                                return (
                                    <td key={`${index}_${nestedField.id}`}
                                        /* fieldValueText expects state, make item look like state */
                                    >{fieldValueLabel({
                                        ...initialFormState,
                                        fieldValues: item
                                    }, field.nestedForm, fieldId)}</td>
                                );
                            })}
                        </tr>
                    ))}
            </div>
            {!(value && value.length > 0) && <p className="text-center"><em>No {field.gpnfEntryLabelPlural}</em></p>}
            {!localDisplayOnly &&
                <div className="d-flex align-items-center">
                    {hasExcelUpload &&
                        <button
                            type="button"
                            className="btn btn-secondary mr-2"
                            onClick={() => {
                                setShowExcelUploadModal(true);
                            }}
                        >
                            Excel Upload
                        </button>}
                    <button type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setFormState({
                                    ...initialFormState,
                                    fieldState: {},
                                    fieldValues: initialFieldValues
                                });
                                setShowAddRecordModal(true);
                            }}
                    >Add {field.gpnfEntryLabelSingular}</button>
                    {additionalButtons && additionalButtons.map((item: JSX.Element) => item)}
                </div>
            }

            {showAddRecordModal &&
                <Modal>
                    <div className="modal-header">
                        <h5 className="modal-title">Add {field.gpnfEntryLabelSingular}</h5>
                        <button type="button"
                                className="close"
                                aria-label="Close"
                                onClick={() => {
                                    setShowAddRecordModal(false);
                                }}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <FormContainer>
                            <Form form={field.nestedForm}
                                  formState={formState}
                                  setFormState={setFormState}
                                  editableFieldIds={field?.gpnfEditableFields}
                                  displayOnly={field.hasOwnProperty('gpnfEditableFields')}
                                  displayFields={field?.gpnfDisplayFields}
                                  displayFieldsMode={field?.gpnfDisplayFieldMode}
                                  showAdministrativeFields={field.hasOwnProperty('gpnfDisplayFields') || showAdministrativeFields}
                            />
                        </FormContainer>
                    </div>
                    <div className="modal-footer">
                        <button type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setShowAddRecordModal(false);
                                }}
                        >Cancel
                        </button>
                        <ActivityButton
                            isActive={formIsSubmitting}
                            disabled={formIsBusy(formState)}
                            className="btn btn-primary"
                            onClick={() => {
                                setFormIsSubmitting(true);
                                if (!validate(field.nestedForm, formState, setFormState)) {
                                    setFormIsSubmitting(false);
                                    return;
                                }
                                // set the new stated for the nested form, if this is a new record,
                                // take current form state to end, if it is an update, replace
                                // edited item with current form state
                                const newState = (formState.fieldValues.hasOwnProperty('__index')) ?
                                    [
                                        ...value.slice(0, formState.fieldValues.__index),
                                        formState.fieldValues,
                                        ...value.slice(formState.fieldValues.__index + 1),
                                    ]
                                    :
                                    [
                                        ...(value ? value : []),
                                        formState.fieldValues
                                    ];

                                // add this record to parent form state
                                onChange && onChange(newState);
                                setFormState({
                                    ...initialFormState,
                                    fieldValues: initialFieldValues
                                });

                                setFormIsSubmitting(false);
                                setShowAddRecordModal(false);
                            }}
                        >Save {field.gpnfEntryLabelSingular}
                        </ActivityButton>
                    </div>
                </Modal>}
            {showExcelUploadModal &&
                <NestedFormExcelUpload
                    field={field}
                    dismiss={() => {
                        setShowExcelUploadModal(false)
                    }}
                    onUploadComplete={(data: object[], meta: any) => {
                        onChange([
                            ...value,
                            ...data
                        ]);
                        setNestedFormExcelUploadErrorMessage(meta?.errorMessage || '');
                    }}
                />}
        </div>
    );
};